import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react'
import type {
  TenantResponse,
  BookmakerResponse,
  PaginatedResponse,
} from '@/api/types'

interface UserSummary {
  id: number
  email: string
  status: string
  created_at: string
}

export function TenantDetailPage() {
  const { t } = useTranslation(['tenants', 'common'])
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user: me } = useAuth()
  const tenantId = Number(id)
  const isPlatformAdmin = me?.platform_role === 'PLATFORM_ADMIN'

  // --- Tenant data ---
  const { data: tenantData, isLoading } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => api.get<TenantResponse>(`/tenants/${tenantId}`),
    enabled: !!tenantId,
  })
  const tenant = tenantData?.data

  // --- Edit state ---
  const [editName, setEditName] = useState<string | null>(null)
  const [editStatus, setEditStatus] = useState<string | null>(null)

  const updateMutation = useMutation({
    mutationFn: (body: { name?: string; status?: string }) => api.put(`/tenants/${tenantId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', tenantId] })
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      setEditName(null)
      setEditStatus(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/tenants/${tenantId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      navigate('/tenants')
    },
  })

  // --- Bookmakers ---
  const [siteOffset, setBookmakerOffset] = useState(0)
  const siteLimit = 10
  const { data: bookmakersData } = useQuery({
    queryKey: ['tenant-bookmakers', tenantId, siteOffset],
    queryFn: () => api.get<PaginatedResponse<BookmakerResponse>>(`/tenants/${tenantId}/bookmakers?offset=${siteOffset}&limit=${siteLimit}`),
    enabled: !!tenantId,
  })
  const bookmakers = bookmakersData?.data.data ?? []
  const bookmakersTotal = bookmakersData?.data.total ?? 0

  const [newBookmakerName, setNewBookmakerName] = useState('')
  const [newBookmakerDomain, setNewBookmakerDomain] = useState('')
  const [siteDialogOpen, setBookmakerDialogOpen] = useState(false)

  const createBookmakerMutation = useMutation({
    mutationFn: () => api.post(`/tenants/${tenantId}/bookmakers`, { name: newBookmakerName, domain: newBookmakerDomain }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-bookmakers', tenantId] })
      setBookmakerDialogOpen(false)
      setNewBookmakerName('')
      setNewBookmakerDomain('')
    },
  })

  const deleteBookmakerMutation = useMutation({
    mutationFn: (siteId: number) => api.delete(`/tenants/${tenantId}/bookmakers/${siteId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-bookmakers', tenantId] }),
  })

  // --- Users ---
  const [userOffset, setUserOffset] = useState(0)
  const userLimit = 10
  const { data: usersData } = useQuery({
    queryKey: ['tenant-users', tenantId, userOffset],
    queryFn: () => api.get<PaginatedResponse<UserSummary>>(`/tenants/${tenantId}/users?offset=${userOffset}&limit=${userLimit}`),
    enabled: !!tenantId,
  })
  const users = usersData?.data.data ?? []
  const usersTotal = usersData?.data.total ?? 0

  const [addUserDialogOpen, setAddUserDialogOpen] = useState(false)
  const [newUserId, setNewUserId] = useState('')

  const addUserMutation = useMutation({
    mutationFn: () => api.post(`/tenants/${tenantId}/users`, { user_id: Number(newUserId) }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] })
      setAddUserDialogOpen(false)
      setNewUserId('')
    },
  })

  const removeUserMutation = useMutation({
    mutationFn: (userId: number) => api.delete(`/tenants/${tenantId}/users/${userId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant-users', tenantId] }),
  })

  // --- Delete confirmation ---
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

  if (isLoading) return <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p>
  if (!tenant) return <p className="text-destructive">{t('notFound')}</p>

  function handleSave() {
    const body: { name?: string; status?: string } = {}
    if (editName !== null && editName !== tenant!.name) body.name = editName
    if (editStatus !== null && editStatus !== tenant!.status) body.status = editStatus
    if (Object.keys(body).length > 0) updateMutation.mutate(body)
  }

  const isEditing = editName !== null || editStatus !== null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/tenants')}>
          <ArrowLeft className="h-4 w-4 mr-1" />{t('buttons.back', { ns: 'common' })}
        </Button>
        <div>
          <h2 className="text-2xl font-semibold">{t('detail.title', { id: tenant.id })}</h2>
          <p className="text-sm text-muted-foreground">{t('detail.subtitle')}</p>
        </div>
        <Badge variant={tenant.status === 'active' ? 'default' : 'secondary'}>{tenant.status}</Badge>
      </div>

      {/* Tenant Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('detail.tenantInfo')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t('labels.name', { ns: 'common' })}</Label>
              <Input
                value={editName ?? tenant.name}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div>
              <Label>{t('labels.status', { ns: 'common' })}</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={editStatus ?? tenant.status}
                onChange={(e) => setEditStatus(e.target.value)}
              >
                <option value="active">active</option>
                <option value="suspended">suspended</option>
              </select>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {t('labels.created', { ns: 'common' })}: {new Date(tenant.created_at).toLocaleString()} | {t('labels.updated', { ns: 'common' })}: {new Date(tenant.updated_at).toLocaleString()}
          </div>
          <div className="flex gap-2">
            {isEditing && (
              <>
                <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
                  <Save className="h-4 w-4 mr-1" />{t('buttons.save', { ns: 'common' })}
                </Button>
                <Button size="sm" variant="outline" onClick={() => { setEditName(null); setEditStatus(null) }}>
                  {t('buttons.cancel', { ns: 'common' })}
                </Button>
              </>
            )}
            {isPlatformAdmin && (
              <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogTrigger render={<Button size="sm" variant="destructive" className="ml-auto" />}>
                  <Trash2 className="h-4 w-4 mr-1" />{t('detail.deleteTenant')}
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>{t('detail.deleteTenant')}</DialogTitle></DialogHeader>
                  <p className="text-sm text-muted-foreground">
                    {t('detail.deleteConfirm', { name: tenant.name })}
                  </p>
                  <div className="flex gap-2 justify-end mt-4">
                    <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>{t('buttons.cancel', { ns: 'common' })}</Button>
                    <Button variant="destructive" onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending}>
                      {t('buttons.delete', { ns: 'common' })}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bookmakers */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">{t('entities.bookmakers', { ns: 'common' })}</CardTitle>
            <p className="text-sm text-muted-foreground">{t('detail.bookmakersSubtitle')}</p>
          </div>
          <Dialog open={siteDialogOpen} onOpenChange={setBookmakerDialogOpen}>
            <DialogTrigger render={<Button size="sm" />}>
              <Plus className="h-4 w-4 mr-1" />{t('detail.newBookmaker')}
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{t('detail.createBookmaker')}</DialogTitle></DialogHeader>
              <form onSubmit={(e) => { e.preventDefault(); createBookmakerMutation.mutate() }} className="flex flex-col gap-4">
                <div>
                  <Label>{t('labels.name', { ns: 'common' })}</Label>
                  <Input value={newBookmakerName} onChange={(e) => setNewBookmakerName(e.target.value)} required />
                </div>
                <div>
                  <Label>{t('labels.domain', { ns: 'common' })}</Label>
                  <Input value={newBookmakerDomain} onChange={(e) => setNewBookmakerDomain(e.target.value)} placeholder={t('placeholders.domainExample', { ns: 'common' })} />
                </div>
                <Button type="submit" disabled={createBookmakerMutation.isPending}>{t('buttons.create', { ns: 'common' })}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {bookmakers.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('detail.noBookmakers')}</p>
          ) : (
            <>
              <div className="border rounded-md">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-medium">{t('labels.name', { ns: 'common' })}</th>
                      <th className="text-left p-3 font-medium">{t('labels.domain', { ns: 'common' })}</th>
                      <th className="text-left p-3 font-medium">{t('labels.status', { ns: 'common' })}</th>
                      <th className="text-left p-3 font-medium">{t('labels.created', { ns: 'common' })}</th>
                      <th className="p-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookmakers.map((s) => (
                      <tr key={s.id} className="border-b last:border-0">
                        <td className="p-3">{s.name}</td>
                        <td className="p-3 text-muted-foreground">{s.domain || '—'}</td>
                        <td className="p-3"><Badge variant={s.status === 'active' ? 'default' : 'secondary'}>{s.status}</Badge></td>
                        <td className="p-3 text-muted-foreground">{new Date(s.created_at).toLocaleDateString()}</td>
                        <td className="p-3">
                          <Button variant="ghost" size="sm" onClick={() => deleteBookmakerMutation.mutate(s.id)} disabled={deleteBookmakerMutation.isPending}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {bookmakersTotal > siteLimit && (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-muted-foreground">{t('labels.total', { ns: 'common' })}: {bookmakersTotal}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={siteOffset === 0} onClick={() => setBookmakerOffset(Math.max(0, siteOffset - siteLimit))}>{t('pagination.previous', { ns: 'common' })}</Button>
                    <Button variant="outline" size="sm" disabled={siteOffset + siteLimit >= bookmakersTotal} onClick={() => setBookmakerOffset(siteOffset + siteLimit)}>{t('pagination.next', { ns: 'common' })}</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Users */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">{t('entities.users', { ns: 'common' })}</CardTitle>
            <p className="text-sm text-muted-foreground">{t('detail.usersSubtitle')}</p>
          </div>
          {isPlatformAdmin && (
            <Dialog open={addUserDialogOpen} onOpenChange={setAddUserDialogOpen}>
              <DialogTrigger render={<Button size="sm" />}>
                <Plus className="h-4 w-4 mr-1" />{t('detail.addUser')}
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t('detail.addUserToTenant')}</DialogTitle></DialogHeader>
                <form onSubmit={(e) => { e.preventDefault(); addUserMutation.mutate() }} className="flex flex-col gap-4">
                  <div>
                    <Label>{t('labels.userId', { ns: 'common' })}</Label>
                    <Input type="number" value={newUserId} onChange={(e) => setNewUserId(e.target.value)} required />
                  </div>
                  <Button type="submit" disabled={addUserMutation.isPending}>{t('buttons.add', { ns: 'common' })}</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('detail.noUsers')}</p>
          ) : (
            <>
              <div className="border rounded-md">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="text-left p-3 font-medium">{t('labels.id', { ns: 'common' })}</th>
                      <th className="text-left p-3 font-medium">{t('labels.email', { ns: 'common' })}</th>
                      <th className="text-left p-3 font-medium">{t('labels.status', { ns: 'common' })}</th>
                      <th className="text-left p-3 font-medium">{t('labels.created', { ns: 'common' })}</th>
                      {isPlatformAdmin && <th className="p-3 w-12"></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b last:border-0 hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/users/${u.id}`)}>
                        <td className="p-3 font-mono text-xs">{u.id}</td>
                        <td className="p-3">{u.email}</td>
                        <td className="p-3"><Badge variant={u.status === 'active' ? 'default' : 'secondary'}>{u.status}</Badge></td>
                        <td className="p-3 text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</td>
                        {isPlatformAdmin && (
                          <td className="p-3" onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" onClick={() => removeUserMutation.mutate(u.id)} disabled={removeUserMutation.isPending}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {usersTotal > userLimit && (
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-muted-foreground">{t('labels.total', { ns: 'common' })}: {usersTotal}</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={userOffset === 0} onClick={() => setUserOffset(Math.max(0, userOffset - userLimit))}>{t('pagination.previous', { ns: 'common' })}</Button>
                    <Button variant="outline" size="sm" disabled={userOffset + userLimit >= usersTotal} onClick={() => setUserOffset(userOffset + userLimit)}>{t('pagination.next', { ns: 'common' })}</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
